import React from 'react';
import { FormattedMessage } from 'react-intl';

import styled from 'styled-components';
import AccordionPanel from '@components/common/app-components/AccordionPanel';

import { useAppDispatch, useAppSelector, useModal, useStateCode } from '@hooks/index';
import EnrolledMembersWrapper from '../EnrolledMembers/EnrolledMembersWrapper';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { PlAN_TYPES } from '@constants/individualPortal/customGrouping/customGrouping.constants';
import UnenrolledMembersWrapper from '../UnenrolledMembers/UnenrolledMembersWrapper';
import GroupedMembersWrapper from '../GroupedMembers/GroupedMembersWrapper';
import useCustomGrouping from '../../hooks/useCustomGrouping';

const StyledAccordionContainer = styled.div`
    & > * {
        border: 0px;
    }
    .header-container {
        background-color: ${({ theme }) => theme.gray10};
        border-radius: 0px;
        padding: 0px 1%;
        letter-spacing: 1px;
    }

    .content {
        border-top: 0px;
        background-color: white;
        padding-bottom: 12px !important;
    }

    .toggle-icon {
        text-decoration: none;
        color: ${({ theme }) => theme.primaryDark};
    }
`;

const StyledAccordionContainerWithBorder = styled.div`
    & > * {
        border: ${({ theme }) => `1px solid ${theme.gray8}`};
    }

    .header-container {
        background-color: ${({ theme }) => theme.gray10};
        border-radius: 0px;
        padding: 0px 1%;
        letter-spacing: 1px;
    }

    .content {
        border-top: 0px;
    }
`;

const HealthPlan = props => {
    const customGroupingState = useAppSelector(state => state.individualPortal.customGrouping);
    const {
        isRenewalApplication = false,
        isAnyHealthGroupEnrolled = false,
        isHealthRenewalGroupAvailable = false,
        healthGroupEnrolledMembersCount = 0,
        allGroupsRenewal = false,
        healthGroups = [],
        unEnrolledHealthGroup = [],
        hasAIMembers = false,
        addMembersGroups = [],
    } = customGroupingState;
    const { openModal } = useModal();
    const { isAIANMemberAlertVisible, isAddMemberWarningVisible, isBoldHeader } = useStateCode();
    const dispatch = useAppDispatch();
    const { areAllMembersAddedToGroups } = useCustomGrouping({ dispatch });
    const {activeSpinnersQty} = useAppSelector(state => state.common.spinner);

    const { daysRemaining, index } = props;

    const AccordionHeader = () => {
        return (
            <p className={`${isBoldHeader ? 'bold-header' : ''}`} data-testid="accordion-header">
                <FormattedMessage
                    id={
                        allGroupsRenewal
                            ? 'customGrouping.coverageYearMemberEnrolled'
                            : 'customGrouping.enrolledMember'
                    }
                    values={{ memberCount: healthGroupEnrolledMembersCount }}
                />
            </p>
        );
    };

    function areAllMembersAddedToHealthGroup() {
        return unEnrolledHealthGroup.every(group => {
            return areAllMembersAddedToGroups(group);
        });
    }

    return (
        <div id={`tabPanel-${index}`} role="tabpanel" tabIndex={0} aria-labelledby={`tab-${index}`}>
            <ConditionalRendering isTrue={isAnyHealthGroupEnrolled}>
                <ConditionalRendering isTrue={isRenewalApplication && isHealthRenewalGroupAvailable}>
                    <p data-testid="renew-change-group">
                        <FormattedMessage id="customGrouping.renewChangeGroup1" />{' '}
                        <a
                            href="javascript:void(0);"
                            onClick={() =>
                                openModal('changeApplicationNotToConsider', { typeOfPlan: PlAN_TYPES.HEALTH })
                            }
                            data-testid="change-application-link"
                        >
                            <FormattedMessage id="customGrouping.renewChangeGroup2" />
                        </a>
                    </p>
                </ConditionalRendering>
                <StyledAccordionContainer data-testid="styled-accordion-container">
                    <AccordionPanel
                        id="healthAccordian"
                        ariaControlsId='healthAccordianPanel'
                        isExpandable={true}
                        header={<AccordionHeader />}
                        isInitialExpanded={addMembersGroups.length === 0}
                        data-testid="accordion-panel-enrolled"
                    >
                        <EnrolledMembersWrapper
                            enrolledGroups={healthGroups}
                            planType={PlAN_TYPES.HEALTH}
                            data-testid="enrolled-members-wrapper"
                        />
                    </AccordionPanel>
                </StyledAccordionContainer>
            </ConditionalRendering>
            <ConditionalRendering isTrue={unEnrolledHealthGroup.length && !areAllMembersAddedToHealthGroup()}>
                <>
                    <p data-testid="whom-you-are-shopping" className={`${isBoldHeader ? 'bold-header' : ''} pb-2 mt-4`}>
                        <FormattedMessage id="customGrouping.whomYouAreShopping" />
                    </p>
                    <ConditionalRendering isTrue={isAIANMemberAlertVisible}>
                        <p data-testid="aian-member-alert">
                            <FormattedMessage
                                id={
                                    hasAIMembers
                                        ? 'customGrouping.aiAnMember1'
                                        : 'customGrouping.aiAnMember2'
                                }
                            />
                        </p>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={!isAIANMemberAlertVisible}>
                        <p data-testid="aian-member2">
                            <FormattedMessage id="customGrouping.aiAnMember2" />
                        </p>
                        <p data-testid="aian-member3">
                            <FormattedMessage id="customGrouping.aiAnMember3" />
                        </p>
                        <p data-testid="aian-member4">
                            <FormattedMessage id="customGrouping.aiAnMember4" />
                        </p>
                        <p data-testid="aian-member5">
                            <FormattedMessage id="customGrouping.aiAnMember5" />
                        </p>
                    </ConditionalRendering>

                    <p data-testid="shop-for-these" className={`${isBoldHeader ? 'bold-header' : ''} pt-2`}>
                        <FormattedMessage id="customGrouping.shopForThese" />
                    </p>

                    <ConditionalRendering isTrue={!!healthGroups.length}>
                        <UnenrolledMembersWrapper
                            unenrolledGroups={unEnrolledHealthGroup}
                            planType={PlAN_TYPES.HEALTH}
                            data-testid="unenrolled-members-wrapper"
                        />
                    </ConditionalRendering>
                </>
            </ConditionalRendering>

            <ConditionalRendering isTrue={!!addMembersGroups.length}>
                <StyledAccordionContainerWithBorder
                    className="mt-4"
                    data-testid="styled-accordion-container-with-border"
                >
                    <AccordionPanel
                        id="healthAccordianUnenrolled"
                        ariaControlsId='healthAccordianUnenrolledPanel'
                        isExpandable={false}
                        header={
                            <p className={`${isBoldHeader ? 'bold-header' : ''}`}>
                                <FormattedMessage id="customGrouping.selectOption" />
                            </p>
                        }
                        isInitialExpanded={true}
                        data-testid="accordion-panel-unenrolled"
                    >
                        <ConditionalRendering isTrue={isAddMemberWarningVisible}>
                            <p data-testid="add-members-warning" className="padding">
                                <FormattedMessage id="customGrouping.addMembers.warning1" />
                            </p>
                            <p className="padding">
                                <FormattedMessage
                                    id="customGrouping.addMembers.warning2"
                                    values={{ daysRemaining }}
                                />
                            </p>
                        </ConditionalRendering>
                        <GroupedMembersWrapper
                            groups={addMembersGroups}
                            planType={PlAN_TYPES.HEALTH}
                            data-testid="grouped-members-wrapper"
                        />
                    </AccordionPanel>
                </StyledAccordionContainerWithBorder>
            </ConditionalRendering>

            <ConditionalRendering isTrue={!healthGroups.length && !activeSpinnersQty}>
                <p className="alert alert-info" data-testid="no-health-group-alert">
                    <FormattedMessage id="customGrouping.noHealthGroup" />
                </p>
            </ConditionalRendering>
        </div>
    );
};

export default HealthPlan;
